import type { LaneId, PlayerObservationV1, VisibleCardViewV1 } from "@fate-kingdom/engine";
import { Canvas } from "@react-three/fiber";

import type { AlphaCombatPresentationState } from "../alpha/battlefield/CombatPresentation";
import type { AlphaBattlefieldCameraState } from "../alpha/camera/battlefield-camera-types";
import type { AlphaCardDragState } from "../alpha/drag/alpha-drag-types";
import type { MatchHudLayout } from "../alpha/hud/hud-layout";
import type { MatchHudModel } from "../alpha/hud/match-hud-model";
import { BattlefieldCameraDirector } from "./BattlefieldCameraDirector";
import { BattlefieldLighting } from "./BattlefieldLighting";
import { ContextLifecycle, type WebGlContextStatus } from "./ContextLifecycle";
import { PerspectiveBattlefield } from "./PerspectiveBattlefield";
import { PlayerHand3D } from "./PlayerHand3D";
import { GameHud3D } from "./GameHud3D";
import type { HandScreenLayout } from "./hand-screen-layout";
import { SceneDiagnostics3D } from "./scene-diagnostics";

interface Battlefield3DProps {
  readonly observation: PlayerObservationV1;
  readonly selectedLaneId: LaneId | undefined;
  readonly legalTargetIds: readonly LaneId[];
  readonly targetMode: "own" | "opponent";
  readonly placementPulseLaneId?: LaneId | undefined;
  readonly attackSourceLaneIds: readonly LaneId[];
  readonly selectedAttackSourceLaneId?: LaneId | undefined;
  readonly combat: AlphaCombatPresentationState | null;
  readonly cameraState: AlphaBattlefieldCameraState;
  readonly reducedMotion: boolean;
  readonly interactionBlocked: boolean;
  readonly selectedHandCardId?: string | undefined;
  readonly handLegalCardIds: ReadonlySet<string>;
  readonly handDragState: AlphaCardDragState;
  readonly draggedHandCard: VisibleCardViewV1 | null;
  readonly handCollapsed: boolean;
  readonly isHumanTurn: boolean;
  readonly focusedHandCardId?: string | undefined;
  readonly hudLayout: MatchHudLayout;
  readonly hudModel: MatchHudModel;
  readonly onChooseLane: (laneId: LaneId) => void;
  readonly onSelectAttack: (laneId: LaneId) => void;
  readonly onInspectCards: (cards: readonly VisibleCardViewV1[]) => void;
  readonly onContextStatusChange: (status: WebGlContextStatus) => void;
  readonly onHandScreenLayoutChange: (layout: HandScreenLayout) => void;
}

export function Battlefield3D({
  observation,
  selectedLaneId,
  legalTargetIds,
  targetMode,
  placementPulseLaneId,
  attackSourceLaneIds,
  selectedAttackSourceLaneId,
  combat,
  cameraState,
  reducedMotion,
  interactionBlocked,
  selectedHandCardId,
  handLegalCardIds,
  handDragState,
  draggedHandCard,
  handCollapsed,
  isHumanTurn,
  focusedHandCardId,
  hudLayout,
  hudModel,
  onChooseLane,
  onSelectAttack,
  onInspectCards,
  onContextStatusChange,
  onHandScreenLayoutChange,
}: Battlefield3DProps) {
  const portrait = typeof window === "undefined" ? true : window.innerHeight > window.innerWidth;
  return (
    <Canvas
      aria-label="Perspective three-lane Fate Kingdom battlefield"
      camera={{ fov: portrait ? 40 : 39, near: 0.1, far: 80, position: [0, 8, 12] }}
      data-near-player={observation.perspectivePlayerId}
      data-perspective-battlefield="physical-v1"
      dpr={portrait ? [1, 1.5] : [1, 2]}
      gl={{
        antialias: true,
        alpha: false,
        powerPreference: "high-performance",
        preserveDrawingBuffer: false,
      }}
      shadows="basic"
    >
      <color attach="background" args={["#050d13"]} />
      <fog attach="fog" args={["#071219", 13, 27]} />
      <BattlefieldLighting />
      <BattlefieldCameraDirector cameraState={cameraState} reducedMotion={reducedMotion} />
      <ContextLifecycle onStatusChange={onContextStatusChange} />
      <SceneDiagnostics3D hudLayout={hudLayout} />
      <PerspectiveBattlefield
        observation={observation}
        legalTargetIds={legalTargetIds}
        selectedLaneId={selectedLaneId}
        targetMode={targetMode}
        placementPulseLaneId={placementPulseLaneId}
        attackSourceLaneIds={attackSourceLaneIds}
        selectedAttackSourceLaneId={selectedAttackSourceLaneId}
        combat={combat}
        reducedMotion={reducedMotion}
        interactionBlocked={interactionBlocked}
        onChooseLane={onChooseLane}
        onSelectAttack={onSelectAttack}
        onInspectCards={onInspectCards}
        transferringCardInstanceId={
          handDragState.status === "dropping"
            ? (handDragState.cardInstanceId ?? undefined)
            : undefined
        }
      />
      <PlayerHand3D
        cards={observation.own.hand}
        draggedCard={draggedHandCard}
        selectedCardId={selectedHandCardId}
        legalCardIds={handLegalCardIds}
        dragState={handDragState}
        collapsed={handCollapsed}
        isHumanTurn={isHumanTurn}
        interactionBlocked={interactionBlocked}
        reducedMotion={reducedMotion}
        focusedCardId={focusedHandCardId}
        onScreenLayoutChange={onHandScreenLayoutChange}
      />
      <GameHud3D layout={hudLayout} model={hudModel} />
    </Canvas>
  );
}
